export const MIXED_CATEGORY_ID = 'mixed';
export const BASE_CORRECT_POINTS = 1000;
export const MAX_TIME_BONUS = 500;
export const STREAK_STEP_BONUS = 120;
export const MAX_STREAK_BONUS = 720;

export function hashSeed(seed = 'arcade-trivia') {
  const text = String(seed);
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

export function createSeededRandom(seed = 'arcade-trivia') {
  let state = hashSeed(seed) || 1;

  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleWithSeed(items, seed = 'arcade-trivia') {
  const random = typeof seed === 'function' ? seed : createSeededRandom(seed);
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
}

export function evaluateAnswer(question, answerId) {
  const correctAnswer = question.answers.find((answer) => answer.correct === true);

  return {
    selectedAnswerId: answerId,
    correctAnswerId: correctAnswer?.id ?? null,
    isCorrect: Boolean(answerId && correctAnswer?.id === answerId),
    timedOut: !answerId,
  };
}

export function calculateTriviaScore({
  isCorrect,
  remainingMs,
  questionMs,
  streak,
}) {
  if (!isCorrect) {
    return {
      points: 0,
      basePoints: 0,
      timeBonus: 0,
      streakBonus: 0,
    };
  }

  const safeQuestionMs = Math.max(1, questionMs);
  const safeRemainingMs = Math.max(0, Math.min(remainingMs, safeQuestionMs));
  const timeBonus = Math.round((safeRemainingMs / safeQuestionMs) * MAX_TIME_BONUS);
  const streakBonus = Math.min(MAX_STREAK_BONUS, Math.max(0, streak - 1) * STREAK_STEP_BONUS);

  return {
    points: BASE_CORRECT_POINTS + timeBonus + streakBonus,
    basePoints: BASE_CORRECT_POINTS,
    timeBonus,
    streakBonus,
  };
}

export function applyAnswerToSummary(summary, answerResult, scoreResult) {
  const nextAnswered = summary.answered + 1;
  const nextCorrect = summary.correct + (answerResult.isCorrect ? 1 : 0);
  const nextStreak = answerResult.isCorrect ? summary.currentStreak + 1 : 0;

  return {
    ...summary,
    answered: nextAnswered,
    correct: nextCorrect,
    score: summary.score + scoreResult.points,
    currentStreak: nextStreak,
    bestStreak: Math.max(summary.bestStreak, nextStreak),
    accuracy: nextAnswered > 0 ? Math.round((nextCorrect / nextAnswered) * 100) : 0,
  };
}

export function createEmptyTriviaSummary(categoryId = MIXED_CATEGORY_ID) {
  return {
    categoryId,
    answered: 0,
    correct: 0,
    score: 0,
    currentStreak: 0,
    bestStreak: 0,
    accuracy: 0,
  };
}

export function isTriviaRecord(score, maxScore) {
  return Number(score) > Number(maxScore ?? 0);
}
